import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, Tabs, useToast, v } from '@kss/ui';
import { Save, ArrowLeft, ChevronRight, ChevronLeft, CheckSquare, Square } from 'lucide-react';
import type {
  Menu,
  Permission,
  Role,
  RoleCreate,
  RoleMenu,
  RolePermission,
} from '@kss/api';
import {
  menuService,
  permissionService,
  roleMenuService,
  rolePermissionService,
  roleService,
} from '@kss/api';

/**
 * RBAC-picker — composite role editor. Steps:
 *
 *   1. Basic       — Name / Code / Description
 *   2. Menus       — checkbox tree; every Menu the role can see in the sidebar
 *   3. Permissions — matrix of (menu × verb) checkboxes; ticks map to
 *                    Permission rows (the seeder produces one per
 *                    `{menuKey}.{verb}` combination and stores the exact
 *                    string in `Permission.code`, which is what the JWT
 *                    `perm[]` claim carries at login).
 *   4. Review      — read-only summary before save.
 *
 * On save we ship the plain role fields plus `menuIds` / `permissionIds`.
 * The backend `RoleController` mirrors the H9 pattern and materialises
 * RoleMenu + RolePermission junction rows in the same request.
 */

const inputCls  = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

const STEPS = ['basic', 'menus', 'permissions', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic',
  menus: 'Menus',
  permissions: 'Permissions',
  review: 'Review',
};

/** Canonical verb order for the permission matrix header. */
const VERBS = ['create', 'view', 'update', 'delete', 'download', 'import', 'export'] as const;
type Verb = (typeof VERBS)[number];

interface MenuNode extends Menu {
  children: MenuNode[];
}

function buildTree(menus: Menu[]): MenuNode[] {
  const nodes = new Map<string, MenuNode>();
  for (const m of menus) {
    if (m.menuId) nodes.set(m.menuId, { ...m, children: [] });
  }
  const roots: MenuNode[] = [];
  for (const n of nodes.values()) {
    if (n.parentId && nodes.has(n.parentId)) nodes.get(n.parentId)!.children.push(n);
    else roots.push(n);
  }
  const sortRec = (arr: MenuNode[]) => {
    arr.sort((a, b) => {
      const sa = a.sortOrder ?? 0;
      const sb = b.sortOrder ?? 0;
      if (sa !== sb) return sa - sb;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    for (const n of arr) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/**
 * Derive the `menuKey` a Permission belongs to from its `code`. Seeder
 * emits `{menuKey}.{verb}` so we can peel the verb off the end and
 * fall back to `module` when the code doesn't fit the pattern (legacy
 * rows).
 */
function menuKeyOfPermission(p: Permission): string {
  const c = (p.code ?? '').toString();
  const dot = c.lastIndexOf('.');
  return dot > 0 ? c.slice(0, dot) : c;
}

function verbOfPermission(p: Permission): string {
  const c = (p.code ?? '').toString();
  const dot = c.lastIndexOf('.');
  const tail = dot > 0 ? c.slice(dot + 1) : '';
  return (tail || p.action || '').toLowerCase();
}

export default function NewRole() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const isEdit = !!id && id !== 'new';

  const [step, setStep] = useState<StepId>('basic');
  const [form, setForm] = useState({ name: '', code: '', description: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Name was presence-only; code and description were unchecked, so
     markup and unbounded strings reached the Role row. Code uses the
     alphanumeric allowlist because it is a machine key ("manager"),
     not free text. */
  const roleErrors: Record<string, string | null> = {
    name: v.runRules(form.name, [v.required('Name'), v.maxLength(80, 'Name'), ...v.safeText('Name')]),
    code: v.runRules(form.code, [v.maxLength(40, 'Code'), v.alphanumericChars('Code')]),
    description: v.runRules(form.description, [
      v.maxLength(500, 'Description'),
      ...v.safeText('Description'),
    ]),
  };
  const roleErr = (k: string) => (touched[k] ? roleErrors[k] ?? undefined : undefined);
  const markR = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));

  const [menus, setMenus] = useState<Menu[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [pickedMenus, setPickedMenus] = useState<Set<string>>(new Set());
  const [pickedPerms, setPickedPerms] = useState<Set<string>>(new Set());

  // On CREATE the RoleId won't exist yet, so we can't fetch the current
  // linkage — pickers start empty. On EDIT we hydrate from the live
  // junction rows so the user sees the role's current state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [m, p] = await Promise.all([
          menuService.all().catch(() => [] as Menu[]),
          permissionService.all().catch(() => [] as Permission[]),
        ]);
        if (cancelled) return;
        setMenus(m ?? []);
        setPermissions(p ?? []);

        if (isEdit && id) {
          const r = await roleService.get(id);
          if (cancelled) return;
          setForm({
            name: r.name ?? '',
            code: r.code ?? '',
            description: r.description ?? '',
            isActive: r.isActive !== false,
          });
          const [rms, rps] = await Promise.all([
            roleMenuService.all().catch(() => [] as RoleMenu[]),
            rolePermissionService.all().catch(() => [] as RolePermission[]),
          ]);
          if (cancelled) return;
          setPickedMenus(
            new Set(
              (rms ?? [])
                .filter((x) => x.roleId === id && x.isActive !== false && x.menuId)
                .map((x) => x.menuId as string),
            ),
          );
          setPickedPerms(
            new Set(
              (rps ?? [])
                .filter((x) => x.roleId === id && x.isActive !== false && x.permissionId)
                .map((x) => x.permissionId as string),
            ),
          );
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Load failed';
        setErr(msg);
        toastRef.current.error('Load failed', msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const tree = useMemo(() => buildTree(menus.filter((m) => m.isActive !== false)), [menus]);

  const permsByMenuKey = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of permissions) {
      const key = menuKeyOfPermission(p);
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [permissions]);

  const menuKeyById = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of menus) {
      // Menu row may not carry menuKey (legacy) — fall back to route/name.
      const key = (row as Menu & { menuKey?: string }).menuKey;
      if (row.menuId && key) m.set(row.menuId, key);
    }
    return m;
  }, [menus]);

  function toggleMenu(menuId: string) {
    setPickedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(menuId)) next.delete(menuId);
      else next.add(menuId);
      return next;
    });
  }

  function togglePerm(permId: string) {
    setPickedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  }

  function selectAllMenus() {
    setPickedMenus(new Set(menus.filter((m) => m.isActive !== false && m.menuId).map((m) => m.menuId as string)));
  }
  function clearMenus() { setPickedMenus(new Set()); }
  function selectAllPerms() { setPickedPerms(new Set(permissions.map((p) => p.permissionId!).filter(Boolean))); }
  function clearPerms() { setPickedPerms(new Set()); }

  /** Toggle every permission for a given menu row. */
  function toggleMenuRow(menuKey: string, checked: boolean) {
    const rowPerms = permsByMenuKey.get(menuKey) ?? [];
    setPickedPerms((prev) => {
      const next = new Set(prev);
      for (const p of rowPerms) {
        const id = p.permissionId;
        if (!id) continue;
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  /** Toggle a whole verb column across every menu that supports it. */
  function toggleVerbColumn(verb: string, checked: boolean) {
    setPickedPerms((prev) => {
      const next = new Set(prev);
      for (const p of permissions) {
        if (verbOfPermission(p) !== verb) continue;
        const id = p.permissionId;
        if (!id) continue;
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const save = async () => {
    setTouched({ name: true, code: true, description: true });
    const problem = Object.values(roleErrors).find(Boolean);
    if (problem) {
      toast.error('Fix errors', problem);
      setStep('basic');
      return;
    }
    setSaving(true);
    try {
      const menuIds = Array.from(pickedMenus);
      const permissionIds = Array.from(pickedPerms);
      // Roles table doesn't carry `isActive` — the strict-mode binder
      // rejects the key. Keep the UI toggle for future use but strip
      // from the payload until the backend adds the column.
      const base = {
        name: form.name,
        code: form.code || undefined,
        description: form.description || undefined,
        menuIds,
        permissionIds,
      };
      if (isEdit && id) {
        await roleService.update([{ roleId: id, ...base } as Role]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        await roleService.create([base as RoleCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/roles');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i >= STEPS.length - 1) return;
    if (step === 'basic') {
      // Block the step advance on any basic-tab error, not just a blank
      // name — otherwise an over-length code only surfaces at save,
      // three steps later, with the offending field off-screen.
      setTouched({ name: true, code: true, description: true });
      const problem = Object.values(roleErrors).find(Boolean);
      if (problem) {
        toast.error('Fix errors', problem);
        return;
      }
    }
    setStep(STEPS[i + 1]);
  };
  const goPrev = () => {
    const i = STEPS.indexOf(step);
    if (i === 0) return;
    setStep(STEPS[i - 1]);
  };

  /* ------------------------------------------------------------------ */
  /*  Panels                                                             */
  /* ------------------------------------------------------------------ */

  const basicPanel = (
    <div className="grid gap-3 md:grid-cols-2">
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onBlur={markR('name')} placeholder="Manager" className={`${inputCls}${roleErr('name') ? ' border-danger' : ''}`} />
        {roleErr('name') && <span className="text-xs text-danger">{roleErr('name')}</span>}
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-content-secondary">Code</span>
        <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} onBlur={markR('code')} placeholder="manager" className={`${inputCls}${roleErr('code') ? ' border-danger' : ''}`} />
        {roleErr('code') && <span className="text-xs text-danger">{roleErr('code')}</span>}
      </label>
      <label className="flex flex-col gap-1 md:col-span-2">
        <span className="text-xs font-medium text-content-secondary">Description</span>
        <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} onBlur={markR('description')} rows={3} className={`${inputCls} h-auto py-2${roleErr('description') ? ' border-danger' : ''}`} />
        {roleErr('description') && <span className="text-xs text-danger">{roleErr('description')}</span>}
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-content-secondary">Status</span>
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'active' }))} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
      </label>
    </div>
  );

  const menuTree = (nodes: MenuNode[], depth = 0) => (
    <ul className={depth === 0 ? 'divide-y divide-border' : ''}>
      {nodes.map((n) => {
        const id = n.menuId as string;
        const checked = pickedMenus.has(id);
        return (
          <li key={id}>
            <label
              className="flex cursor-pointer items-center gap-2 py-1.5 text-sm hover:bg-surface-card"
              style={{ paddingLeft: `${8 + depth * 20}px` }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleMenu(id)}
                className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
              />
              <span className="font-medium text-content-primary">{n.name}</span>
              {n.route && <span className="text-xs text-content-muted">{n.route}</span>}
            </label>
            {n.children.length > 0 && menuTree(n.children, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  const menuPanel = (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs text-content-muted">
          {pickedMenus.size} of {menus.filter((m) => m.isActive !== false).length} menus selected.
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={selectAllMenus}><CheckSquare className="h-3.5 w-3.5" /> All</Button>
          <Button size="sm" variant="outline" onClick={clearMenus}><Square className="h-3.5 w-3.5" /> None</Button>
        </div>
      </div>
      <div className="max-h-[520px] overflow-y-auto rounded-md border border-border bg-surface-card-hover">
        {menus.length === 0
          ? <div className="p-6 text-center text-sm text-content-muted">No menus. Seed the tenant via <code>/panel/tenants/migrate</code>.</div>
          : menuTree(tree)}
      </div>
    </div>
  );

  /* Permission matrix — one row per menu that has permissions and
     one column per verb. Only picked menus are shown so the role
     picker composes cleanly (menu access = row visible; verb ticks =
     what the role can DO on that menu). Selecting all verbs on every
     picked menu is the common "grant everything" shortcut. */
  const pickedMenuKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const id of pickedMenus) {
      const k = menuKeyById.get(id);
      if (k) keys.add(k);
    }
    return keys;
  }, [pickedMenus, menuKeyById]);

  const permRows = useMemo(() => {
    const rows: { menuKey: string; menuName: string; perms: Permission[] }[] = [];
    const menuByKey = new Map<string, Menu>();
    for (const m of menus) {
      const k = (m as Menu & { menuKey?: string }).menuKey;
      if (k) menuByKey.set(k, m);
    }
    // If the user hasn't picked any menus yet, fall back to every menu that
    // has permissions so the matrix is still previewable.
    const scope = pickedMenuKeys.size > 0
      ? Array.from(pickedMenuKeys)
      : Array.from(permsByMenuKey.keys());
    for (const key of scope) {
      const perms = permsByMenuKey.get(key) ?? [];
      const menu = menuByKey.get(key);
      const displayName = menu?.name ?? key;
      rows.push({ menuKey: key, menuName: displayName, perms });
    }
    rows.sort((a, b) => a.menuKey.localeCompare(b.menuKey));
    return rows;
  }, [pickedMenuKeys, permsByMenuKey, menus]);

  const permsPanel = (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-content-muted">
          {pickedPerms.size} of {permissions.length} permissions granted.
          {pickedMenuKeys.size === 0 && (
            <span className="ml-2 text-warning">Pick menus first to see just the relevant rows.</span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={selectAllPerms}><CheckSquare className="h-3.5 w-3.5" /> All</Button>
          <Button size="sm" variant="outline" onClick={clearPerms}><Square className="h-3.5 w-3.5" /> None</Button>
        </div>
      </div>
      <div className="max-h-[520px] overflow-auto rounded-md border border-border bg-surface-card-hover">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-surface-card">
            <tr className="text-left text-xs font-semibold uppercase text-content-secondary">
              <th className="px-3 py-2">Menu</th>
              {VERBS.map((v) => (
                <th key={v} className="px-2 py-2 text-center capitalize">
                  <div className="flex flex-col items-center gap-1">
                    <span>{v}</span>
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer rounded border-border text-primary focus:ring-primary"
                      title={`Toggle all ${v}`}
                      onChange={(e) => toggleVerbColumn(v, e.target.checked)}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {permRows.length === 0 && (
              <tr>
                <td colSpan={VERBS.length + 1} className="px-3 py-6 text-center text-content-muted">
                  No permissions available for the selected menus.
                </td>
              </tr>
            )}
            {permRows.map((row) => {
              const permByVerb = new Map<Verb, Permission | undefined>();
              for (const v of VERBS) {
                permByVerb.set(v, row.perms.find((p) => verbOfPermission(p) === v));
              }
              const rowAllChecked =
                row.perms.length > 0 &&
                row.perms.every((p) => p.permissionId && pickedPerms.has(p.permissionId));
              return (
                <tr key={row.menuKey} className="hover:bg-surface-card">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={rowAllChecked}
                        onChange={(e) => toggleMenuRow(row.menuKey, e.target.checked)}
                        className="h-3.5 w-3.5 cursor-pointer rounded border-border text-primary focus:ring-primary"
                        title="Toggle every verb for this menu"
                      />
                      <div className="flex flex-col">
                        <span className="font-medium text-content-primary">{row.menuName}</span>
                        <span className="font-mono text-[10px] text-content-muted">{row.menuKey}</span>
                      </div>
                    </div>
                  </td>
                  {VERBS.map((v) => {
                    const p = permByVerb.get(v);
                    if (!p?.permissionId) {
                      return <td key={v} className="px-2 py-2 text-center text-content-muted">—</td>;
                    }
                    const on = pickedPerms.has(p.permissionId);
                    return (
                      <td key={v} className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => togglePerm(p.permissionId!)}
                          className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const reviewPanel = (
    <div className="grid gap-3">
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <ReviewRow label="Name" value={form.name} />
          <ReviewRow label="Code" value={form.code || '—'} />
          <ReviewRow label="Description" value={form.description || '—'} />
          <ReviewRow label="Status" value={form.isActive ? 'active' : 'inactive'} />
          <ReviewRow label="Menus" value={`${pickedMenus.size} selected`} />
          <ReviewRow label="Permissions" value={`${pickedPerms.size} granted`} />
        </div>
      </div>
      <Alert variant="info">
        <span className="text-xs">
          Save writes the role, then materialises {pickedMenus.size} RoleMenu row(s) and
          {' '}{pickedPerms.size} RolePermission row(s). Users assigned this role receive
          the union of their roles' permissions in the JWT <code>perm[]</code> claim on
          next login.
        </span>
      </Alert>
    </div>
  );

  const panels: Record<StepId, React.ReactNode> = {
    basic: basicPanel,
    menus: menuPanel,
    permissions: permsPanel,
    review: reviewPanel,
  };
  const tabs = STEPS.map((s) => ({ id: s, label: STEP_LABELS[s], content: panels[s] }));
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step} onChange={(sid) => setStep(sid as StepId)} barOnly />
      </div>
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/roles')}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-base font-semibold text-content-primary">{isEdit ? 'Edit role' : 'New role'}</h2>
        </div>
        <div className="min-h-[220px]">
          {loading ? <div className="text-sm text-content-muted">Loading…</div> : panels[step]}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst || saving}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext} disabled={saving}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/roles')} disabled={saving}>Cancel</Button>
            <Button type="button" onClick={save} disabled={saving || !form.name.trim()}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value}</span>
    </div>
  );
}
